/**
 * Validador IBAN para cuentas bancarias españolas
 * Implementa el algoritmo oficial de validación IBAN
 */

/**
 * Convierte letras a números según el estándar IBAN
 * A=10, B=11, C=12, ..., Z=35
 */
function letterToNumber(letter) {
  return letter.charCodeAt(0) - 55; // A=65 -> 10, B=66 -> 11, etc.
}

/**
 * Calcula el módulo 97 para números grandes (como string)
 */
function mod97(str) {
  let remainder = 0;
  for (let i = 0; i < str.length; i++) {
    remainder = (remainder * 10 + parseInt(str[i])) % 97;
  }
  return remainder;
}

/**
 * Valida un IBAN español
 * @param {string} iban - IBAN a validar
 * @returns {boolean} - true si el IBAN es válido
 */
export function validateIBAN(iban) {
  if (!iban) return false;
  
  // Limpiar espacios y convertir a mayúsculas
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // Verificar longitud (España: 24 caracteres)
  if (cleanIban.length !== 24) return false;
  
  // Verificar que empiece por ES
  if (!cleanIban.startsWith('ES')) return false;
  
  // Verificar formato: ES + 2 dígitos + 20 caracteres alfanuméricos
  if (!/^ES\d{2}[A-Z0-9]{20}$/.test(cleanIban)) return false;
  
  // Algoritmo de validación IBAN
  // 1. Mover los primeros 4 caracteres al final
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);
  
  // 2. Convertir letras a números
  let numericString = '';
  for (let char of rearranged) {
    if (/[A-Z]/.test(char)) {
      numericString += letterToNumber(char);
    } else {
      numericString += char;
    }
  }
  
  // 3. Calcular módulo 97
  const remainder = mod97(numericString);
  
  // 4. El IBAN es válido si el resto es 1
  return remainder === 1;
}

/**
 * Valida específicamente un IBAN español con estructura bancaria
 * @param {string} iban - IBAN a validar
 * @returns {object} - Objeto con resultado de validación y detalles
 */
export function validateSpanishIBAN(iban) {
  if (!iban) {
    return {
      isValid: false,
      error: 'IBAN requerido'
    };
  }
  
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // Verificar longitud
  if (cleanIban.length !== 24) {
    return {
      isValid: false,
      error: 'El IBAN español debe tener exactamente 24 caracteres'
    };
  }
  
  // Verificar código de país
  if (!cleanIban.startsWith('ES')) {
    return {
      isValid: false,
      error: 'El IBAN debe comenzar por ES (España)'
    };
  }
  
  // Verificar formato
  if (!/^ES\d{22}$/.test(cleanIban)) {
    return {
      isValid: false,
      error: 'Formato inválido. Debe ser: ES + 22 dígitos'
    };
  }
  
  // Validar algoritmo IBAN
  if (!validateIBAN(cleanIban)) {
    return {
      isValid: false,
      error: 'Los dígitos de control del IBAN no son válidos'
    };
  }
  
  return {
    isValid: true,
    iban: cleanIban,
    formatted: formatIBAN(cleanIban)
  };
}

/**
 * Formatea un IBAN con espacios para mejor legibilidad
 * @param {string} iban - IBAN a formatear
 * @returns {string} - IBAN formateado
 */
export function formatIBAN(iban) {
  if (!iban) return '';
  
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // Formatear en grupos de 4 caracteres
  return cleanIban.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Formatea el input del usuario en tiempo real
 * @param {string} value - Valor del input
 * @returns {string} - Valor formateado
 */
export function formatIBANInput(value) {
  if (!value) return '';
  
  // Eliminar espacios y convertir a mayúsculas
  let clean = value.replace(/\s/g, '').toUpperCase();
  
  // Limitar a 24 caracteres
  clean = clean.slice(0, 24);
  
  // Solo permitir ES al inicio + números
  if (clean.length >= 2) {
    if (!clean.startsWith('ES')) {
      clean = 'ES' + clean.replace(/[^0-9]/g, '').slice(0, 22);
    } else {
      clean = 'ES' + clean.slice(2).replace(/[^0-9]/g, '').slice(0, 22);
    }
  } else if (clean.length === 1) {
    clean = clean === 'E' ? 'E' : 'ES';
  }
  
  // Formatear con espacios
  return formatIBAN(clean);
} 